import { KeyRound } from "lucide-react";
import { FormProvider, UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FloatingLabelInput,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { TPasswordRequest } from "../../settings.types";

interface IPasswordCardProps {
  passwordForm: UseFormReturn<TPasswordRequest>;
  onSubmitPassword: (data: TPasswordRequest) => Promise<void>;
}

export function PasswordCard({
  passwordForm,
  onSubmitPassword,
}: IPasswordCardProps) {
  const { t } = useI18n();

  return (
    <Card className="flex-1 bg-background">
      <CardHeader className="flex flex-row items-center gap-4">
        <KeyRound className="w-8 h-8" />
        <CardTitle>{t("settings.security.passwordManagement")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4 pt-4 border-t">
          <h3 className="text-lg font-medium">
            {t("settings.security.changePassword")}
          </h3>
          <FormProvider {...passwordForm}>
            <form
              onSubmit={passwordForm.handleSubmit(onSubmitPassword)}
              className="space-y-4"
            >
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("settings.security.currentPassword")}
                        type="password"
                        placeholder="••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("settings.security.newPassword")}
                        type="password"
                        placeholder="••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("settings.security.confirmNewPassword")}
                        type="password"
                        placeholder="••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full">
                {t("settings.security.changePassword")}
              </Button>
            </form>
          </FormProvider>
        </div>
      </CardContent>
    </Card>
  );
}
